import { useCallback, useEffect, useRef, useState } from "react";

export interface ItemSizeUL {
  upperLeftPosition: { x: number; y: number };
  width: number;
  height: number;
}

export const getMaxElemWidthByRef = (ref?: any): number | null => {
  if (ref && ref.current) {
    const parent = ref.current.parentElement; // Get the parent element
    if (parent) {
      const parentRect = parent.getBoundingClientRect(); // Parent's dimensions
      const computedStyles = window.getComputedStyle(ref.current); // Current element's styles

      // Calculate max width considering padding and margins
      const maxWidth = parentRect.width - Number.parseFloat(computedStyles.marginLeft) - Number.parseFloat(computedStyles.marginRight);

      return maxWidth;
    }
  }
  return null; // Return null if no valid ref or parent
};

export const getElemSizeULByRef = (ref: any): ItemSizeUL => {
  if (ref && ref.current) {
    const rect = ref.current.getBoundingClientRect();
    const x = rect.left;
    const y = rect.top;
    const w = rect.width;
    const h = rect.height;
    return { upperLeftPosition: { x, y }, width: w, height: h };
  }
  return { upperLeftPosition: { x: 0, y: 0 }, width: 0, height: 0 };
};

//returns the fixed position of the upper left corner of the element, as well as its width and height
export function useItemSizeUL(ref?: React.RefObject<HTMLElement | null>, enable = true, otherDependencies: any[] = []): ItemSizeUL {
  const [upperLeftPosition, setUpperLeftPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);

  // Use ref for the handler so ResizeObserver always calls the latest version
  const handleResizeRef = useRef<() => void>(() => {});
  handleResizeRef.current = useCallback(() => {
    if (!ref?.current) return;
    const { upperLeftPosition: XY, width: w, height: h } = getElemSizeULByRef(ref);
    // Allow zero sizes during early layout/hidden states so size can recover on next observer pass.
    if (!XY || !Number.isFinite(w) || !Number.isFinite(h)) return;
    setUpperLeftPosition(XY);
    setWidth(w);
    setHeight(h);
  }, [ref]);

  useEffect(() => {
    if (!enable || !ref) return;

    const handleResize = () => handleResizeRef.current();

    // Initial measurement
    handleResize();

    // Retry after a short delay in case element isn't laid out yet
    const timeoutId = setTimeout(handleResize, 100);

    // ResizeObserver: re-measure when element dimensions change
    // (e.g., when a parent accordion opens and the element goes from 0 to actual width)
    let resizeObserver: ResizeObserver | undefined;
    if (ref.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(ref.current);
    }

    // MutationObserver on document.body for attribute changes
    const mutationObserver = new MutationObserver(handleResize);
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: false,
      subtree: false,
    });

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [ref, enable, ...otherDependencies]);

  return { upperLeftPosition, width, height };
}
